/**
 * Route calculation utilities for distance, elevation, and formatting
 */

/**
 * Calculate distance between two coordinates using Haversine formula
 * @param coords1 First coordinate [longitude, latitude]
 * @param coords2 Second coordinate [longitude, latitude]
 * @returns Distance in meters
 */
export function calculateDistance(
  coords1: [number, number],
  coords2: [number, number]
): number {
  const [lng1, lat1] = coords1
  const [lng2, lat2] = coords2

  const R = 6371000 // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1)
  const dLon = toRadians(lng2 - lng1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  const distance = R * c

  return distance
}

/**
 * Calculate total elevation gain from waypoint array
 * @param waypoints Array of waypoints with elevation property
 * @returns Total elevation gain in feet
 */
export function calculateElevationGain(waypoints: Array<{ elevation?: number | null }>): number {
  if (waypoints.length < 2) return 0

  let totalGain = 0
  for (let i = 1; i < waypoints.length; i++) {
    const prevElevation = waypoints[i - 1].elevation
    const currElevation = waypoints[i].elevation

    if (
      typeof prevElevation === 'number' &&
      typeof currElevation === 'number' &&
      currElevation > prevElevation
    ) {
      totalGain += currElevation - prevElevation
    }
  }

  return totalGain
}

/**
 * Calculate total distance for a route with multiple waypoints
 * @param waypoints Array of waypoints with latitude and longitude
 * @returns Total distance in meters
 */
export function calculateTotalDistance(
  waypoints: Array<{ latitude: number; longitude: number }>
): number {
  if (waypoints.length < 2) return 0

  let totalDistance = 0
  for (let i = 1; i < waypoints.length; i++) {
    const prev = waypoints[i - 1]
    const curr = waypoints[i]
    totalDistance += calculateDistance([prev.longitude, prev.latitude], [curr.longitude, curr.latitude])
  }

  return totalDistance
}

/**
 * Format distance in meters to human-readable string
 * @param meters Distance in meters
 * @returns Formatted string (e.g., "5.2 mi" or "1,234 ft")
 */
export function formatDistance(meters: number): string {
  const miles = meters * 0.000621371
  const feet = meters * 3.28084

  if (miles >= 0.1) {
    return `${miles.toFixed(1)} mi`
  } else {
    return `${Math.round(feet).toLocaleString()} ft`
  }
}

/**
 * Format elevation in feet to human-readable string
 * @param feet Elevation in feet
 * @returns Formatted string (e.g., "1,234 ft" or "0 ft")
 */
export function formatElevation(feet: number): string {
  return `${Math.round(feet).toLocaleString()} ft`
}

/**
 * Convert degrees to radians
 */
function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}

